import type { MuiTheme as Theme } from '@paintscout/ui'

/**
 * Style primitives shared by the Good/Better/Best template editor and its panels. Each
 * component keeps its own sheet; this holds only what more than one of them needs.
 */

type Palette = Theme['paintscout']['palette']['brand']

/**
 * The editor swaps whole controls at the breakpoint rather than reflowing them. Note MUI v4's
 * `down(key)` takes the *next* breakpoint's value, so `down('xs')` is the one that ends at the
 * theme's `sm` (790); `down('sm')` would run all the way to `md` (958).
 */
export const hideOnMobile = (theme: Theme) => ({
  [theme.breakpoints.down('xs')]: { display: 'none' }
})

export const mobileOnly = (theme: Theme) => ({
  display: 'none',
  [theme.breakpoints.down('xs')]: { display: 'block' }
})

/**
 * A control that only appears on hover is unreachable on a touch screen, which never fires
 * `mouseenter`. This is a separate axis from the width breakpoint above: a desktop browser
 * dragged down to phone width still has a pointer, and should keep the hover reveal. Paired
 * with `pointer: coarse` to match the query the DropdownSelect components already use.
 */
export const TOUCH_QUERY = '@media (hover: none) and (pointer: coarse)'

/**
 * Flex shells. Between them these two shapes account for a third of the rules in this folder,
 * so they're spread in at the point the `display` would otherwise have been declared — later
 * properties still override them.
 */
export const column = (gap?: number) => ({
  display: 'flex' as const,
  flexDirection: 'column' as const,
  ...(gap === undefined ? {} : { gap })
})

export const row = (gap?: number) => ({
  display: 'flex' as const,
  alignItems: 'center' as const,
  ...(gap === undefined ? {} : { gap })
})

/** Strip the browser's button chrome — every control in this editor is a bare <button>. */
export const resetButton = {
  appearance: 'none' as const,
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer'
}

/**
 * The editor is built from one repeated shape: a small inline-flex chip that fills with P96
 * when hovered or selected. Sizes and radii differ per row, so callers layer those on top.
 */
export function chip(theme: Theme, { Neutral }: Palette) {
  return {
    ...resetButton,
    display: 'inline-flex',
    alignItems: 'center',
    color: Neutral.P60,
    fontSize: theme.typography.pxToRem(14),
    transition: 'background-color 0.15s',
    '&:hover:not(:disabled)': {
      background: Neutral.P96
    }
  }
}

/**
 * Shell behind the tier actions menu, the feature menu and the Configure menu. Callers add
 * their own anchoring (left/right/top) and z-index.
 */
export function popover(theme: Theme, { Neutral }: Palette) {
  return {
    position: 'absolute' as const,
    background: theme.palette.common.white,
    border: `1px solid ${Neutral.P90}`,
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(20,30,50,0.12)'
  }
}

/**
 * Input paints its border, hover ring and focus ring on the MuiInput root, and InputField
 * forwards `className` — not `classes` — to that element. So this has to be passed as
 * className to strip the box; the doubled selector then beats Input's own single-class rule.
 */
export const bareInput = {
  '&&, &&:hover:not(:focus-within), &&:focus-within': {
    border: 'none',
    background: 'transparent',
    boxShadow: 'none'
  },
  '&& input': {
    padding: 0,
    background: 'transparent'
  }
}

/** The 28px glyph tiles used by the properties bar's icon row. */
export function iconOptionStyles(theme: Theme, brand: Palette) {
  const { Neutral, Blue } = brand
  return {
    iconOption: {
      ...chip(theme, brand),
      width: 28,
      height: 28,
      justifyContent: 'center',
      borderRadius: 8
    },
    iconOptionSelected: {
      background: Neutral.P96,
      color: Blue.Blue55
    }
  }
}
